import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"
import { getUtms } from "@/lib/getUtms"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-extrabold uppercase transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-black",
        secondary:
          "bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white",
        blue: "bg-[var(--blue)] text-white hover:bg-[var(--blue)]/90",
        disabled: "bg-[var(--gray)] text-white cursor-not-allowed",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border-2 border-primary bg-white text-primary hover:bg-primary hover:text-white",
        ghost:
          "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[54px] px-[25px] py-[13px] text-[22px] rounded-[12px]",
        sm: "h-[40px] px-[15px] pt-[12.5px] pb-[10.5px] text-[15px] rounded-[8px]",
        lg: "h-[61px] px-[26px] py-[16px] text-[24px] rounded-[14px]",
        icon: "size-[54px] min-w-0 px-0 rounded-[12px]",
        "icon-sm": "size-[34px] min-w-0 px-0 rounded-[8px]",
        "icon-lg": "size-[61px] min-w-0 px-0 rounded-[14px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  onClick,
  ...props
}) {
  const Comp = asChild ? Slot : "button"

  const handleClick = (e) => {
    setTimeout(() => {
      if (window.dataLayer) {
        const utms = getUtms() || {};
        const target = e.currentTarget;
        window.dataLayer.push({
          event: "cta_click",
          cta_name: target.title || target.textContent?.trim() || "",
          cta_url: target.getAttribute("href") || "submit",
          utm_source: utms.utm_source || undefined,
          utm_medium: utms.utm_medium || undefined,
          utm_campaign: utms.utm_campaign || undefined,
          utm_content: utms.utm_content || undefined,
        });
      }
    }, 0);
    onClick?.(e);
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={handleClick}
      {...props} />
  );
}

export { Button, buttonVariants }
